import { VFC, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grid,
  Box,
  FormControl,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  IconButton,
  Container,
} from "@mui/material";
import { MdmUserCreateFormSchema, MdmUserEditForm, MdmUserEditFormSchema } from "./api/MdmUserForm";
import { PullDownItem } from "../common/form/FormPullDown";
import { ResultDialog, ResultResponse } from "../common/dialog/ResultDialog";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TenantService } from "../tenant/api/TenantService";
import { TenantNameRequest, TenantNameResponse } from "../tenant/api/TenantForm";
import { AxiosResponse } from "axios";
import { Utility } from "../../utils/Utility";
import { OrganizationNameRequest, OrganizationNameResponse } from "../organization/OrganizationForm";
import { OrganizationService } from "../organization/api/OrganizationService";
import { MdmUserService } from "./api/MdmUserService";
import MdmUserDialogMode from "../../entity/MdmUserDialogMode";
import { useLocation } from "react-router-dom";
import Constants from "../../utils/Constants";
import { FormLabel } from "../common/form/FormLabel";
import { AlertDialog } from "../common/dialog/AlertDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { ACCESS_AUTH, IS_MFAAUTH, TENANT_LOCK } from "../../entity/MdmUser";

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EditOffOutlinedIcon from '@mui/icons-material/EditOffOutlined';
import { ProtectedApiAuth } from "../protected/ProtectedApiAuth";

type Props = {
  isAlertDialogOpen: boolean;
  mode: string;
  mdmUserId: string;
  defaultDialogMode: MdmUserDialogMode;
  handleAlertCancel: () => void;
  handleAlertOk: () => void;
};

/**
 * 確認ダイアログ
 * @param param0
 * @returns
 */
const MdmUserDialog: VFC<Props> = ({
  isAlertDialogOpen,
  mode,
  mdmUserId,
  defaultDialogMode,
  handleAlertCancel,
  handleAlertOk,
}) => {

  const mfaOptions = Utility.getIsMfaAuthOption();
  const tenantLockOptions = Utility.getMdmUserTenantLockOption();
  const accessAuthOptions = Utility.getMdmUserAccessAuth();
  const CONTENT_WIDTH = 7;
  const LABEL_WIDTH = 5;

  /***************************************************************
   * State管理
   **************************************************************/

  // ダイヤログモード
  const [dialogMode, setDialogMode] = useState<MdmUserDialogMode>(
    defaultDialogMode !== null || mdmUserId !== "" ? defaultDialogMode : MdmUserDialogMode.CREATE
  );

  // テナントID
  const [tenantId] = useState(sessionStorage.getItem("TENANT_ID") ?? "");

  // MDMユーザー情報
  const [mdmUserEditForm, setMdmUserEditForm] = useState(new MdmUserEditForm());

  // 企業選択肢
  const [tenantOptions, setTenantOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // 組織選択肢
  // const [organizationOptions, setOrganizationOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // ユーザーレベル選択肢
  const [userLevelOption, setUserLevelOption] = useState<PullDownItem[]>();

  // ステータス選択肢
  const [lockStatusOption, setLockStatusOption] = useState<PullDownItem[]>();

  // 新規作成・編集・複製確認ダイアログ
  const [isEditAlertDialogOpen, setIsEditAlertDialogOpen] = useState(false);

  // 削除確認ダイアログ
  const [isDeleteAlertDialogOpen, setIsDeleteAlertDialogOpen] = useState(false);

  // 共通ダイアログ
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // パスワード表示用
  const [showPassword, setShowPassword] = useState(false);

  // パスワード確認表示用
  const [showPasswordAgain, setShowPasswordAgain] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    if (resultResponse.status === "OK") { 
      handleCancel();
      handleAlertOk();
    }
    setIsDialogOpen(false);
  };

  // バックエンドからのメッセージを設定
  const setBackendValidMessage = (response: any) => {
    let error = response;
    if (error.fullName) {
      setError("fullName", { message: error.fullName });
    }
    if (error.email) {
      setError("email", { message: error.email });
    }
    if (error.password) {
      setError("password", { message: error.password });
    }
    if (error.passwordMinLength) {
      setError("password", { message: error.passwordMinLength });
    }
    if (error.passwordMaxLength) {
      setError("password", { message: error.passwordMaxLength });
    }
    if (error.passwordPattern) {
      setError("password", { message: error.passwordPattern });
    }
    if (error.passwordCharacterPattern) {
      setError("password", { message: error.passwordCharacterPattern });
    }
    if (error.passwordNoMatch) {
      setError("password", { message: error.passwordNoMatch });
    }
    if (error.passwordAgain) {
      setError("passwordAgain", { message: error.passwordAgain });
    }
    if (error.tenantId) {
      setError("tenantId", { message: error.tenantId });
    }
    if (error.orgId) {
      setError("orgId", { message: error.orgId });
    }
    if (error.roleCd) {
      setError("roleCd", { message: error.roleCd });
    }
    if (error.tenantLock) {
      setError("tenantLock", {message: error.tenantLock});
    }
    if (error.lockStatus) {
      setError("lockStatus", { message: error.lockStatus });
    }
    if (error.expireDate) {
      setError("expireDate", { message: error.expireDate });
    }
    if (error.comment) {
      setError("comment", { message: error.comment });
    }
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  // キャンセル処理
  const handleCancel = () => {
    if (dialogMode === MdmUserDialogMode.CREATE) {
      reset({
        userId: "",
        fullName: "",
        email: "",
        password: "",
        passwordAgain: "",
        tenantId: "",
        orgId: "",
        roleCd: "",
        lockStatus: "",
        isMfaauth: "",
        expireDate: "",
        expireDateView: null,
        comment: ""
      });
      setShowPassword(false);
      setShowPasswordAgain(false);
    } else {
      setDialogMode(MdmUserDialogMode.DETAIL);
    }
    handleAlertCancel();
  }
  
  // パスワード表示切替
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  // パスワード確認表示切替
  const handleClickShowPasswordAgain = () => setShowPasswordAgain(!showPasswordAgain);

  /***************************************************************
     * 初期表示処理
     **************************************************************/
  useEffect(() => {
    setDialogMode(defaultDialogMode);
    getTenantNameList();
    // getOrganizationNameList();
    setLockStatusOption(Utility.getLockStatusOption());
    if ("" !== mdmUserId) {
      // テナント情報取得
      findByEditId();
    }
  }, [mdmUserId]);

  useEffect(() => {
    if (isAlertDialogOpen) {
      if (dialogMode === MdmUserDialogMode.CREATE) {
      const initialPassword = generatePassword();
      reset({
        ...new MdmUserEditForm(),
        password: initialPassword,
        passwordAgain: initialPassword,
        tenantId: tenantId,
        lockStatus: "0",
        isMfaauth: "false",
      });
      setShowPassword(true);
      setShowPasswordAgain(true);
      }
    }
  }, [isAlertDialogOpen, dialogMode]);

  // 企業名一覧取得API
  const getTenantNameList = () => {
    const service = new TenantService();
    service
      .findTenantNameList(new TenantNameRequest)
      .then((response: AxiosResponse) => {
        const tenantNameList: TenantNameResponse[] = response.data.data;
        setTenantOptions(Utility.getTenantNameOption(tenantNameList));
      })
      .catch((err) => {})
      .finally(() => {});
  }

  // 組織名一覧取得API
  // const getOrganizationNameList = () => {
  //   const service = new OrganizationService();
  //   service
  //     .findOrganizationNameList(new OrganizationNameRequest)
  //     .then((response: AxiosResponse) => {
  //       const organizationNameList: OrganizationNameResponse[] = response.data.data;
  //       setOrganizationOptions(Utility.getOrganizaitonNameOption(organizationNameList));
  //     })
  //     .catch((err) => {})
  //     .finally(() => {}); 
  // }

  // ユーザーレベル選択肢設定
  const setUserLevel = async (tenantId: Number) => {
    const res = await Utility.getRoleOption(tenantId);
    setUserLevelOption(res);
  };

  // テナント情報取得API
  const findByEditId = () => {
    const service = new MdmUserService();
    service
      .findByEditId(mdmUserId)
      .then(async (response: AxiosResponse) => {
        const mdmUser = response.data.result;

        // 権限情報を取得
        // 権限リストを先に取得しないとroleCdの表示ができないためawaitで待機
        await setUserLevel(mdmUser.tenantId);

        setValue("userId", mdmUserId);
        setValue("fullName", mdmUser.fullName);
        setValue("email", mdmUser.email);
        setValue("password", mdmUser.password);
        setValue("passwordAgain", mdmUser.password);
        setValue("tenantId", mdmUser.tenantId);
        setValue("tenantName", mdmUser.tenantName);
        setValue("orgId", mdmUser.orgId);
        setValue("organizationName", mdmUser.organizationName);
        setValue("userLevel", mdmUser.userLevel);
        setValue("tenantLock", mdmUser.tenantLock);
        setValue("roleCd", mdmUser.roleCd);
        setValue("expireDate", mdmUser.expireDate);
        setValue("expireDateView", mdmUser.expireDate === null || mdmUser.expireDate === "" ? null : new Date(mdmUser.expireDate));
        setValue("tenantLock", mdmUser.tenantLock);
        setValue("lockStatus", mdmUser.lockStatus);
        setValue("isMfaauth", mdmUser.isMfaauth);
        setValue("comment", mdmUser.comment);
        setValue("createUser", mdmUser.createUser);
        setValue("createDate", mdmUser.createDate);
        setValue("updateUser", mdmUser.updateUser);
        setValue("updateDate", mdmUser.updateDate);
        setValue("accessAuth", mdmUser.accessAuth);

        setMdmUserEditForm(response.data.result);

      })
      .catch((err) => {})
      .finally(() => {});
  };

  // パスワード生成
  const generatePassword = () => {
    const charset = "1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ!$%&#";
    const numbers = "1234567890";
    const letters = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let password = numbers.charAt(Math.floor(Math.random() * numbers.length)); // 数字をランダムに1文字選択
    password += letters.charAt(Math.floor(Math.random() * letters.length)); // 英字をランダムに1文字選択
    for (let i = 0; i < 10; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length)); // 残りをランダムに選択
    }
    return password.split('').sort(() => Math.random() - 0.5).join(''); // パスワードをシャッフル
  };

  /***************************************************************
   * 新規作成・編集処理
   **************************************************************/
  const handleEditAlertOpen = () => {
    setIsEditAlertDialogOpen(true);
  };
  // 新規作成・編集確認ダイアログ(CLOSE)
  const handleEditAlertClose = () => {
    setIsEditAlertDialogOpen(false);
  };
  // 保存ボタン押下(入力チェック)
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    reset,
    control,
    formState: { errors },
  } = useForm<MdmUserEditForm>({
    resolver: yupResolver(
      dialogMode === MdmUserDialogMode.EDIT ?  MdmUserEditFormSchema : MdmUserCreateFormSchema
    ),
    reValidateMode: "onSubmit",
    defaultValues: new MdmUserEditForm(),
  });

  // テナント情報新規作成確認API or テナント情報編集確認API
  const handleEditBtn: SubmitHandler<MdmUserEditForm> = (hookForm) => {
    setValue("expireDate", hookForm.expireDateView ? Utility.registDateFormat(hookForm.expireDateView.toLocaleString()) : "");
    const service = new MdmUserService();
    switch (dialogMode) {
      case MdmUserDialogMode.CREATE:
        // 新規作成
        service
          .createConfirm(hookForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setMdmUserEditForm(getValues());
              handleEditAlertOpen();
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
      case MdmUserDialogMode.EDIT:
      // テナント情報編集確認API
        service
          .updateConfirm(hookForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setMdmUserEditForm(getValues());
              handleEditAlertOpen();
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
    }
  };
  // テナント情報新規作成API or テナント情報編集API
  const handleEditConfirmBtn = () => {
    const service = new MdmUserService();

    handleEditAlertClose();
    setMdmUserEditForm(getValues());

    switch (dialogMode) {
      case MdmUserDialogMode.CREATE:
        service
          .create(mdmUserEditForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setIsDialogOpen(true);
              setResultResponse(response.data);
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
      case MdmUserDialogMode.EDIT:
        // テナント情報編集API
        service
          .update(mdmUserEditForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setIsDialogOpen(true);
              setResultResponse(response.data);
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
    }
  };

  /***************************************************************
   * 削除処理
   **************************************************************/
  // 削除確認ダイアログ(OPEN)
  const handleDeleteAlertOpen = () => {
    setIsDeleteAlertDialogOpen(true);
  };
  // 削除確認ダイアログ(CLOSE)
  const handleDeleteAlertClose = () => {
    setIsDeleteAlertDialogOpen(false);
  };
  // 削除ボタン押下
  const handleDeleteBtn = () => {
    const service = new MdmUserService();

    if ("" !== mdmUserId) {
      setIsDeleteAlertDialogOpen(false);
      service
        .delete(mdmUserId)
        .then((response) => {
          if ("OK" === response.data.status) {
            setIsDialogOpen(true);
            setResultResponse(response.data);
          } else {
            setIsDialogOpen(true);
            setResultResponse(response.data);
          }
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          // TODO 必要ならローディング
        });
    }
  };

  /***************************************************************
   * 編集処理
   **************************************************************/
  const handleChangeEditMode = () => {
    setDialogMode(dialogMode === MdmUserDialogMode.DETAIL ? MdmUserDialogMode.EDIT : MdmUserDialogMode.DETAIL);
  }

  /***************************************************************
   * 権限処理
   **************************************************************/
  // 権限なしのオーバーライド
  const overrideFunction = (response: ResultResponse) => {
    setResultResponse(response);
    setIsDialogOpen(true);
  }

  /***************************************************************
   * レイアウト
   **************************************************************/

  // ダイヤログ名
  const dialogName = (dialogMode === MdmUserDialogMode.CREATE) ? "LiNKアカウント追加" :
                     (dialogMode === MdmUserDialogMode.DETAIL) ? "LiNKアカウント詳細" :
                                                                 "LiNKアカウント編集";

  return (
    <>
      <Dialog 
        open={isAlertDialogOpen}
        fullWidth
      >
        <DialogTitle sx={{ py: 1 }}>
          <Grid container justifyContent={"space-between"}>
            <Grid item xs={false}>{dialogName}</Grid>
            {/* ボタン */}
            {(dialogMode !== MdmUserDialogMode.CREATE) && <>
              <Grid item xs={false}>
                <Container>
                  <ProtectedApiAuth
                    authcd={Constants.AUTH_MDM_USER_DELETE}
                    defaultFunction={handleDeleteAlertOpen}
                    overrideFunction={overrideFunction}
                  >
                    <IconButton>
                      <DeleteOutlinedIcon/>
                    </IconButton>
                  </ProtectedApiAuth>
                  <ProtectedApiAuth
                    authcd={Constants.AUTH_MDM_USER_EDIT}
                    defaultFunction={handleChangeEditMode}
                    overrideFunction={overrideFunction}
                  >
                    <IconButton>
                      {dialogMode === MdmUserDialogMode.DETAIL ? <>
                        <EditOutlinedIcon/>
                      </> : <>
                        <EditOffOutlinedIcon/>
                      </>}
                    </IconButton>
                  </ProtectedApiAuth>
                </Container>
              </Grid>
            </>}
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ py: 0 }}>
          <Box>
            {/* 入力フォーム */}
            <Box sx={{ py: 0, px: 4 }}>
              <Box sx={{ py: 0, px: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  {/* ユーザーID */}
                  {dialogMode !== MdmUserDialogMode.CREATE &&
                    <>
                      <FormLabel xs={LABEL_WIDTH} label="ユーザーID" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(mdmUserId)}
                        </Typography>
                      </Grid>
                    </>
                  }
                  {/* 氏名 */}
                  <FormLabel xs={LABEL_WIDTH} label="氏名" isMandatory={dialogMode !== MdmUserDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === MdmUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                            {Utility.NullEmptyStringWrap(mdmUserEditForm.fullName)}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <TextField
                            fullWidth
                            size="small"
                            {...register("fullName")}
                            error={"tenantName" in errors}
                            helperText={errors.fullName?.message}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  {/* メールアドレス */}
                  <FormLabel xs={LABEL_WIDTH} label="メールアドレス" isMandatory={dialogMode !== MdmUserDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === MdmUserDialogMode.DETAIL ? <>
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(mdmUserEditForm.email)}
                        </Typography>
                      </Grid>
                    </> : <>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          size="small"
                          {...register("email")}
                          error={"email" in errors}
                          helperText={errors.email?.message}
                        />
                      </FormControl>
                    </>}
                  </Grid>
                  {dialogMode === MdmUserDialogMode.CREATE && (
                    <>
                      {/* パスワード */}
                      <FormLabel xs={LABEL_WIDTH} label="パスワード" isMandatory={true} />
                      <Grid item xs={CONTENT_WIDTH}>
                        <TextField
                          fullWidth
                          size="small"
                          {...register("password")}
                          error={"password" in errors}
                          helperText={errors.password?.message}
                          type={showPassword ? "text" : "password"}
                          InputProps={{ 
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                >
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      {/* パスワード確認用 */}
                      <FormLabel xs={LABEL_WIDTH} label="パスワード（確認用）" isMandatory={true} />
                      <Grid item xs={CONTENT_WIDTH}>
                        <TextField
                          fullWidth
                          size="small"
                          {...register("passwordAgain")}
                          error={"passwordAgain" in errors}
                          helperText={errors.passwordAgain?.message}
                          type={showPasswordAgain ? "text" : "password"}
                          InputProps={{ 
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPasswordAgain}
                                >
                                  {showPasswordAgain ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                    </>
                  )}
                  {/* テナント名 */}
                  <FormLabel xs={LABEL_WIDTH} label="テナント名" isMandatory={dialogMode !== MdmUserDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === MdmUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(
                            tenantOptions?.filter(item => item.key === mdmUserEditForm.tenantId)[0] !== undefined ? 
                            tenantOptions?.filter(item => item.key === mdmUserEditForm.tenantId)[0].value : ""
                          )}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <Controller
                            control={control}
                            name="tenantId"
                            defaultValue=""
                            render={({ field }) => (
                              <>
                                <Select
                                  size="small"
                                  {...field}
                                  {...register("tenantId")}
                                  error={"companyId" in errors}
                                  // onChange={}
                                  onChange={(event, child) => {
                                    if (Number.isSafeInteger(event.target.value)) {
                                      setValue("tenantId",event.target.value)
                                      setValue("userLevel", "")
                                      setValue("roleCd", "")
                                      setUserLevel(parseInt(event.target.value))
                                    }
                                  }}
                                >
                                  <MenuItem value="">選択してください</MenuItem>
                                  {tenantOptions?.map((item) => (
                                    <MenuItem key={item.key} value={item.key}>
                                      {item.value}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {errors.tenantId && (
                                  <FormHelperText error>
                                    {errors.tenantId?.message}
                                  </FormHelperText>
                                )}
                              </>
                            )}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  {/* 組織名 */}
                  {/* <FormLabel xs={LABEL_WIDTH} label="組織名" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === MdmUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                          {organizationOptions?.filter(item => item.key === mdmUserEditForm.orgId)[0] !== undefined ? 
                          organizationOptions?.filter(item => item.key === mdmUserEditForm.orgId)[0].value : ""}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <Controller
                            control={control}
                            name="orgId"
                            defaultValue=""
                            render={({ field }) => (
                              <>
                                <Select
                                  size="small"
                                  {...field}
                                  {...register("orgId")}
                                >
                                  <MenuItem value="">選択してください</MenuItem>
                                  {organizationOptions?.map((item) => (
                                    <MenuItem key={item.key} value={item.key}>
                                      {item.value}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </>
                            )}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid> */}

                  {/* アクセス権限 */}
                  <FormLabel xs={LABEL_WIDTH} label="アクセス権限" isMandatory={dialogMode !== MdmUserDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                  {dialogMode === MdmUserDialogMode.DETAIL ? <>
                    <Grid item xs={CONTENT_WIDTH}>
                      <Typography noWrap component="div" align="left">
                        {Utility.NullEmptyStringWrap(ACCESS_AUTH[mdmUserEditForm.accessAuth])}
                      </Typography>
                    </Grid>
                  </> : <>
                    <FormControl fullWidth>
                    <Controller
                        control={control}
                        name="accessAuth"
                        defaultValue={0}
                        render={({ field }) => (
                          <>
                            <Select
                              size="small"
                              {...field}
                              {...register("accessAuth")}
                              error={"accessAuth" in errors}
                            >
                              {accessAuthOptions?.map((item) => (
                                <MenuItem key={item.key} value={item.key}>
                                  {item.value}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.accessAuth && (
                              <FormHelperText error>
                                {errors.accessAuth?.message}
                              </FormHelperText>
                            )}
                          </>
                        )}
                      />
                    </FormControl>
                  </>}
                  </Grid>

                  {/* 権限 */}
                  <FormLabel xs={LABEL_WIDTH} label="権限" isMandatory={dialogMode !== MdmUserDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === MdmUserDialogMode.DETAIL ? <>
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                        {Utility.NullEmptyStringWrap(
                          userLevelOption?.filter(item => item.key === mdmUserEditForm.roleCd)[0] !== undefined ? 
                          userLevelOption?.filter(item => item.key === mdmUserEditForm.roleCd)[0].value : ""
                        )}
                        </Typography>
                      </Grid>
                    </> : <>
                      <FormControl fullWidth>
                        <Controller
                          control={control}
                          name="roleCd"
                          defaultValue=""
                          render={({ field }) => (
                            <>
                              <Select size="small" {...field}>
                                <MenuItem value="">選択してください</MenuItem>
                                {userLevelOption
                                  ? userLevelOption.map((item) => (
                                      <MenuItem key={item.key} value={item.key}>
                                        {item.value}
                                      </MenuItem>
                                    ))
                                  : ""}
                              </Select>
                              {errors.roleCd && (
                                <FormHelperText error>
                                  {errors.roleCd?.message}
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                      </FormControl>
                    </>}
                  </Grid>

                  {/* テナント都合によるロック */}
                  {/* テナント管理側で制御し、現時点では使用しないため非表示にする
                  <FormLabel xs={LABEL_WIDTH} label="テナントロック" />
                  <Grid item xs={CONTENT_WIDTH}>
                  {dialogMode === MdmUserDialogMode.DETAIL ? <>
                    <Grid item xs={CONTENT_WIDTH}>
                      <Typography noWrap component="div" align="left">
                        {Utility.NullEmptyStringWrap(TENANT_LOCK[mdmUserEditForm.tenantLock])}
                      </Typography>
                    </Grid>
                  </> : <>
                    <FormControl fullWidth>
                      <Controller
                        control={control}
                        name="tenantLock"
                        defaultValue=""
                        render={({ field }) => (
                          <>
                            <Select
                              size="small"
                              {...field}
                              {...register("tenantLock")}
                              error={"tenantLock" in errors}
                            >
                              <MenuItem value="">選択してください</MenuItem>
                              {tenantLockOptions?.map((item) => (
                                <MenuItem key={item.key} value={item.key}>
                                  {item.value}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.tenantLock && (
                              <FormHelperText error>
                                {errors.tenantLock?.message}
                              </FormHelperText>
                            )}
                          </>
                        )}
                      />
                    </FormControl>
                  </>}
                  </Grid>
                  */}
                  {/* ロックステータス */}
                  <FormLabel xs={LABEL_WIDTH} label="ロックステータス" isMandatory={dialogMode !== MdmUserDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === MdmUserDialogMode.DETAIL ? <>
                    {/* {false ? <> */}
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(
                            lockStatusOption?.filter(item => item.key === mdmUserEditForm.lockStatus)[0] !== undefined ? 
                            lockStatusOption?.filter(item => item.key === mdmUserEditForm.lockStatus)[0].value : ""
                          )}
                        </Typography>
                      </Grid>
                    </> : <>
                      <FormControl fullWidth>
                        <Controller
                          control={control}
                          name="lockStatus"
                          defaultValue=""
                          render={({ field }) => (
                            <>
                              <Select size="small" {...field}>
                                <MenuItem value="">選択してください</MenuItem>
                                {lockStatusOption
                                  ? lockStatusOption
                                      .filter(item => {
                                        if (mdmUserEditForm.lockStatus === "2" && dialogMode === MdmUserDialogMode.EDIT) {
                                          return true; // パスワードロックの場合は全項目表示
                                        }
                                        return ["0", "1"].includes(item.key);
                                      })
                                      .map((item) => (
                                      <MenuItem key={item.key} value={item.key}>
                                        {item.value}
                                      </MenuItem>
                                    ))
                                  : []}
                              </Select>
                              {errors.lockStatus && (
                                <FormHelperText error>
                                  {errors.lockStatus?.message}
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                      </FormControl>
                    </>}
                  </Grid>
                  {/* MFA認証実施可否 */}
                  <FormLabel xs={LABEL_WIDTH} label="MFA認証実施可否" isMandatory={dialogMode !== MdmUserDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === MdmUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                            {Utility.NullEmptyStringWrap(IS_MFAAUTH[mdmUserEditForm.isMfaauth])}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <Controller
                            control={control}
                            name="isMfaauth"
                            defaultValue=""
                            render={({ field }) => (
                              <>
                                <Select
                                  size="small"
                                  {...field}
                                  {...register("isMfaauth")}
                                  error={"isMfaauth" in errors}
                                >
                                  <MenuItem value="">選択してください</MenuItem>
                                  {mfaOptions?.map((item) => (
                                    <MenuItem key={item.key} value={item.key}>
                                      {item.value}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {errors.isMfaauth && (
                                  <FormHelperText error>
                                    {errors.isMfaauth?.message}
                                  </FormHelperText>
                                )}
                              </>
                            )}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  {/* アカウント有効期限 */}
                  <FormLabel xs={LABEL_WIDTH} label="アカウント有効期限" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === MdmUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                            {Utility.NullEmptyStringWrap(Utility.dateFormat(mdmUserEditForm.expireDate))}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <Controller
                            name="expireDateView"
                            control={control}
                            render={({ field }) => (
                              <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={ja}
                                dateFormats={{ monthAndYear: "yyyy年 MM月" }}
                                localeText={{
                                  previousMonth: "前月を表示",
                                  nextMonth: "次月を表示",
                                }}
                              >
                                <DatePicker
                                  slotProps={{ textField: { size: 'small' } }} 
                                  {...field}/>
                              </LocalizationProvider>
                            )}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  
                  {/* 備考 */}
                  <FormLabel xs={LABEL_WIDTH} label="備考" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === MdmUserDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                            {Utility.NullEmptyStringWrap(mdmUserEditForm.comment)}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <TextField
                            fullWidth
                            size="small"
                            {...register("comment")}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  {dialogMode !== MdmUserDialogMode.CREATE &&
                    <>
                      {/* 作成者 */}
                      <FormLabel xs={LABEL_WIDTH} label="作成者" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(mdmUserEditForm.createUser)}
                        </Typography>
                      </Grid>
                      {/* 作成日時 */}
                      <FormLabel xs={LABEL_WIDTH} label="作成日時" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(mdmUserEditForm.createDate)}
                        </Typography>
                      </Grid>
                      {/* 更新者 */}
                      <FormLabel xs={LABEL_WIDTH} label="更新者" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(mdmUserEditForm.updateUser)}
                        </Typography>
                      </Grid>
                      {/* 更新日時 */}
                      <FormLabel xs={LABEL_WIDTH} label="更新日時" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(mdmUserEditForm.updateDate)}
                        </Typography>
                      </Grid>
                      {/* 削除日時 */}
                      <FormLabel xs={LABEL_WIDTH} label="削除日時" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(mdmUserEditForm.deleteDate)}
                        </Typography>
                      </Grid>
                    </>
                  }
                </Grid>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>キャンセル</Button>
          {dialogMode !== MdmUserDialogMode.DETAIL && <>
            <Button onClick={handleSubmit(handleEditBtn)}>{mode}</Button>
          </>}
        </DialogActions>
      </Dialog>

      {/* 新規作成・編集確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isEditAlertDialogOpen}
        title="保存確認"
        message="保存しますか？"
        mode="保存"
        handleAlertCancel={handleEditAlertClose}
        handleAlertOk={handleEditConfirmBtn}
      />

      {/* 削除確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isDeleteAlertDialogOpen}
        title="削除確認"
        message="削除しますか？"
        mode="削除"
        handleAlertCancel={handleDeleteAlertClose}
        handleAlertOk={handleDeleteBtn}
      />

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
    </>
  );
};

export default MdmUserDialog;